import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Helmet from '../../components/Helmet';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useGetQuestionnaireQuery } from '../../services/questionnaire';

import { Questionnaire as ClassityQuestionnaire } from '@classity-edtech/ui-questionnaire';
import { parseToken, style, useLocalStorage, windowExists } from '@classity-edtech/ui-base';
import { useGetProfileQuery, useLazyGetProfileQuery } from '../../services/profile';
import { CircularProgress } from '@mui/material';
import { FreeFormName } from '@classity-edtech/platform-utils';
import {
  useLazyGetResponseFromCacheQuery,
  useResetResponseCacheMutation,
  useSubmitResponseMutation,
} from '../../services/response';
import { useLazyRelatedUsersQuery } from '../../services/users';
import { webPaths } from '../../routes/paths';
import { Roles } from '../../utils/enum';

export interface HomeProps extends MuiBoxProps {}

export const Questionnaire = ({ className }: HomeProps) => {
  const navigate = useNavigate();

  const [userFlow] = useLocalStorage('userFlow', '');
  const [resetResponse] = useResetResponseCacheMutation();
  const getFreeformKey = (name: string) => {
    return Object.keys(FreeFormName).find(key => FreeFormName[key] === name);
  };
  const [searchParams, setSearchParams] = useSearchParams();

  const [lastQuestionnaire, setLastQuestionnaire] = useLocalStorage('lastQuestionnaire', '');
  const token = windowExists && parseToken((windowExists?.localStorage as any)?.getItem('token'));
  const [relatedUsers] = useLazyRelatedUsersQuery();
  const [submitResponse] = useSubmitResponseMutation();
  const [getProfile] = useLazyGetProfileQuery();
  const [cacheResponse] = useLazyGetResponseFromCacheQuery();
  const { data: profile, isLoading: loadingProfile } = useGetProfileQuery(
    {
      userId: token.user,
    },
    { skip: !token.user || token.exp * 1000 < new Date().getTime() },
  );
  const { data: form, isLoading: loadingForm } = useGetQuestionnaireQuery(
    {
      name: getFreeformKey(searchParams.get('formName')),
    },
    { skip: !searchParams.get('formName') },
  );

  const handleNavigation = async (screen: string) => {
    if (screen === '/checkout') return navigate(`/${webPaths.checkout}`);
    setSearchParams({ screen, formName: searchParams.get('formName') });
    setLastQuestionnaire({ screen, formName: searchParams.get('formName') });
  };

  const getUserRole = (role: string) => {
    switch (role) {
      case Roles.PARENT:
        return 'parent';
      case Roles.STUDENT:
        return 'student';

      default:
        return '';
    }
  };

  const authAction = async (role: string, user: string, token: string, isSignIn: boolean) => {
    if (token) {
      localStorage.setItem('token', token);
    }

    const tokenUser = parseToken(token);

    const getProfileResult = await getProfile({ userId: tokenUser.user });

    if (!isSignIn) {
      (windowExists?.dataLayer as any)?.push({
        event: 'register',
        traffic_type: process.env.NODE_ENV === 'production' ? 'production' : 'local',
        user_id: getProfileResult?.data?.ids?.find(id => id.name === 'uid').value,
        role: getUserRole(tokenUser.role),
        registered: !!token,
      });
    }

    const nextScreen =
      Number(form?.controls?.findIndex(f => f.name === searchParams.get('screen')?.toString())) + 1;

    handleNavigation(`${nextScreen}`);
  };

  const emailVerificationAction = async () => {
    resetResponse({ userId: token.user });
    localStorage.removeItem('lastQuestionnaire');
    localStorage.removeItem('responses');
    localStorage.removeItem('userFlow');

    (windowExists?.dataLayer as any)?.push({
      event: 'verify_email',
      traffic_type: process.env.NODE_ENV === 'production' ? 'production' : 'local',
      user_id: token.uid,
      role: getUserRole(token.role),
      registered: !!token,
    });

    navigate(`/${webPaths.emailConfirmation}`);
  };

  const handleSubmitResponse = async () => {
    const cacheResponseResult = await cacheResponse({ userId: token.user });

    if ('data' in cacheResponseResult) {
      const response = cacheResponseResult?.data;

      const roleControl = form?.controls?.find(c => c.name == 'roleId');

      const roleObject = {
        label: roleControl?.name,
        position: roleControl?.position,
        question: roleControl?.displayName,
        value: {
          name: [roleControl?.input?.find(i => i.value)?.name],
          value: [roleControl?.input?.find(i => i.value)?.value],
        },
      };

      const payload = {
        responseInput: {
          report: [...response?.[response?.length - 1].filter(r => r.label !== 'meta'), roleObject],
          formName: getFreeformKey(searchParams.get('formName')),
        },
      };

      const res = await submitResponse(payload);

      if ('data' in res) {
        setTimeout(async () => {
          const relatedUsersResult = await relatedUsers({ userId: token.user });
          const latestRelation = relatedUsersResult?.data
            ?.slice()
            ?.sort(
              (a, b) => new Date(b.meta.created).getTime() - new Date(a.meta.created).getTime(),
            )[0];

          (windowExists?.dataLayer as any)?.push({
            event: 'register',
            traffic_type: process.env.NODE_ENV === 'production' ? 'production' : 'local',
            user_id: latestRelation?.profile?.uid,
            role: getUserRole(latestRelation?.roleId),
            registered: true,
          });
        }, 5000);
      }
    }
  };

  useEffect(() => {
    if (!!lastQuestionnaire.screen) {
      const screenBeforeAuth = `${form?.controls[form?.controls.findIndex(c => c.name === 'auth') - 1].position}`;

      if (!profile && parseInt(screenBeforeAuth) < parseInt(lastQuestionnaire.screen)) {
        setSearchParams({
          screen: screenBeforeAuth,
          formName: lastQuestionnaire.formName,
        });
      } else {
        setSearchParams({
          screen: lastQuestionnaire.screen,
          formName: lastQuestionnaire.formName,
        });
      }
    }
  }, [profile, form]);

  return (
    <>
      <Helmet title='Classity: Nurturing the next generation of science and tech leaders' />
      {loadingProfile || loadingForm ? (
        <Box className={`${className} questionnaire__loader`}>
          <CircularProgress />
        </Box>
      ) : (
        <></>
      )}
    </>
  );
};

Questionnaire.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Questionnaire.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Questionnaire)(
  ({ theme }) => `
  &.MuiBox-root {
    background-color: ${theme.palette.background.paper};
  }

  &.questionnaire__loader {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    background-color: ${style.colors.darkGray[50]}
  }
`,
);
